import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  IconButton,
  Tabs,
  Tab,
  Chip,
} from "@mui/material";
import ApiClient from "../../apis/Api-client";
import { errorToster, successToster } from "../../shared/toster/Toster";
import { useSelector } from "react-redux";
import { selectAuthData } from "../../core/store/redux/slices/Auth-slice";
import InfiniteScroll from "react-infinite-scroll-component";
import IconConstants from "../../utils/IconConstants";
import { AttachFile, Reply, Search, Send, ArrowBack } from "@mui/icons-material";
import { motion } from "framer-motion";
import { END_POINT } from "../../utils/End-points";

const EmailList = () => {
  const { api } = ApiClient();
  const { hotel } = useSelector(selectAuthData);
  const [emails, setEmails] = useState([]);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [emailConfigured, setEmailConfigured] = useState(true);
  const [search, setSearch] = useState("");
  const [nextPageToken, setNextPageToken] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [replyEmail, setReplyEmail] = useState(false);
  const [composeEmail, setComposeEmail] = useState(false);
  const [composeTo, setComposeTo] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [tab, setTab] = useState("inbox");
  const [showEmailList, setShowEmailList] = useState(true);

  useEffect(() => {
    fetchEmails(true);
    setSelectedEmail(null);
  }, [tab, search]);

  const fetchEmails = async (isNewSearch = false) => {
    try {
      const res = await api.get(END_POINT.GOOGLE.EMAILS, {
        type: tab,
        q: search,
        pageToken: isNewSearch ? undefined : nextPageToken,
      });
      if (res?.data?.data) {
        setEmailConfigured(true);
        const { emails, nextPageToken } = res?.data?.data;
        setEmails((prev) => (isNewSearch ? [...emails] : [...prev, ...emails]));
        setNextPageToken(nextPageToken);
        setHasMore(!!nextPageToken);
      }
    } catch (err) {
      if (err?.response?.status === 404 || err?.response?.status === 403) {
        setEmails([]);
        setNextPageToken(null);
        setHasMore(false);
      } else {
        console.error("Fetch emails failed:", err);
        setEmailConfigured(false);
      }
    }
  };

  const connectGmail = async () => {
    try {
      const res = await api.get(END_POINT.GOOGLE.AUTH_URL, {
        hotel_email: hotel?.hotel_email,
      });
      if (res.status === 200) {
        window.open(res.data.url, "Google Auth", "width=500,height=500");
        const handler = (e) => {
          if (e.key === "google-auth-status" && e.newValue === "success") {
            setEmailConfigured(true);
            fetchEmails(true);
            localStorage.removeItem("google-auth-status");
          }
        };
        window.addEventListener("storage", handler);
      }
    } catch (err) {
      errorToster(err.message);
    }
  };

  const handleFileChange = (e) => {
    if (!e.target.files) return;
    const newFiles = Array.from(e.target.files);
    setAttachments((prev) => [...prev, ...newFiles]);
  };

  const handleRemoveFile = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSendEmail = async (toEmail) => {
    if (!toEmail) {
      errorToster("Please select a recipient");
      return;
    }
    const formData = new FormData();
    formData.append("to", toEmail);
    formData.append("subject", subject);
    formData.append("message", message);
    attachments.forEach((file) => {
      formData.append("attachments", file);
    });

    try {
      await api.postFormData(END_POINT.GOOGLE.SEND_MAIL, formData);
      successToster("Email sent");
      setReplyEmail(false);
      setComposeEmail(false);
      setSubject("");
      setMessage("");
      setComposeTo("");
      setAttachments([]);
    } catch (err) {
      errorToster(err.message || "Failed to send email");
    }
  };

  const handleEmailSelect = (email) => {
    setSelectedEmail(email);
    setReplyEmail(false);
    setShowEmailList(false);
  };

  const handleBackToList = () => {
    setShowEmailList(true);
    setSelectedEmail(null);
    setComposeEmail(false);
  };

  if (!emailConfigured) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        }}
      >
        <Button
          onClick={connectGmail}
          variant="contained"
          sx={{
            textTransform: "none",
            px: { xs: 3, sm: 4 },
            py: { xs: 1, sm: 1.5 },
            borderRadius: 2,
            fontSize: { xs: "0.875rem", sm: "1rem" },
          }}
        >
          Connect Gmail
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        height: { xs: "90dvh", sm: "87dvh" },
        bgcolor: "#f5f5f5",
        border: "1px solid #dadce0",
        minHeight: { xs: "calc(100vh - 105px)", sm: "calc(100vh - 126px)" },
        flexDirection: { xs: "column", sm: "row" },
      }}
    >
      {/* Email List Section */}
      <Box
        sx={{
          width: { xs: "100%", sm: "42%", md: "32%" },
          bgcolor: "white",
          borderRight: { xs: "none", sm: "1px solid #dadce0" },
          borderBottom: { xs: "1px solid #dadce0", sm: "none" },
          position: "relative",
          height: { xs: showEmailList ? "100%" : "0", sm: "100%" },
          display: { xs: showEmailList ? "block" : "none", sm: "block" },
          overflow: "hidden",
        }}
      >
        <Tabs
          value={tab}
          onChange={(e, newValue) => setTab(newValue)}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
          sx={{ borderBottom: "1px solid #dadce0" }}
        >
          <Tab
            value="inbox"
            label="Inbox"
            sx={{
              textTransform: "none",
              fontSize: { xs: "0.8rem", sm: "0.85rem" },
            }}
          />
          <Tab
            value="sent"
            label="Sent"
            sx={{
              textTransform: "none",
              fontSize: { xs: "0.8rem", sm: "0.85rem" },
            }}
          />
        </Tabs>

        {emails.length === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              height: { xs: "calc(100vh - 105px)", sm: "calc(100vh - 126px)" },
              backgroundColor: "#f0f0f0",
              position: "relative",
            }}
          >
            <Typography
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                fontSize: { xs: "0.875rem", sm: "1rem" },
              }}
              color="text.secondary"
            >
              No emails found
            </Typography>
          </Box>
        ) : (
          <>
            <Box
              sx={{
                p: { xs: "2.2%", sm: "3.2%" },
                borderBottom: "1px solid #dadce0",
              }}
            >
              <TextField
                placeholder="Search mail"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                fullWidth
                size="small" 
                autoComplete="off"
                InputProps={{
                  startAdornment: (
                    <Search
                      sx={{
                        color: "#5f6368",
                        mr: 1,
                        fontSize: { xs: "1rem", sm: "1.25rem" },
                      }}
                    />
                  ),
                  sx: {
                    bgcolor: "#f1f3f4",
                    borderRadius: 2,
                    border: "none",
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    "& fieldset": { border: "none" },
                  },
                }}
              />
            </Box>
            <Box
              sx={{
                height: { xs: "calc(100vh - 105px)", sm: "calc(100vh - 126px)" },
                overflow: "hidden",
              }}
            >
              <InfiniteScroll
                dataLength={emails.length}
                next={() => fetchEmails()}
                hasMore={hasMore}
                loader={
                  <Box
                    sx={{
                      p: 1,
                      textAlign: "center",
                      fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    }}
                  >
                    Loading...
                  </Box>
                }
                height="calc(100vh - 105px)"
                style={{ overflow: "auto" }}
              >
                {emails.map((email) => {
                  const isActive = selectedEmail?.id === email.id;
                  return (
                    <Box
                      key={email.id}
                      onClick={() => handleEmailSelect(email)}
                      sx={{
                        p: { xs: "1rem", sm: "1.4rem", md: "1.85rem" },
                        borderBottom: "1px solid #f0f0f0",
                        cursor: "pointer",
                        bgcolor: isActive ? "#e8f0fe" : "transparent",
                        "&:hover": { bgcolor: "#f8f9fa" },
                        display: "flex",
                        gap: { xs: 1, sm: 2 },
                      }}
                    >
                      <Box
                        sx={{
                          width: { xs: "2.1rem", sm: "2.3rem", md: "2.5rem" },
                          height: { xs: "2.1rem", sm: "2.3rem", md: "2.5rem" },
                          borderRadius: "50%",
                          bgcolor: isActive ? "#FFFFFF" : "#F5F6FA",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <Typography
                          sx={{
                            color: isActive ? "#1C1F26" : "#818592",
                            fontSize: { xs: "0.6rem", sm: "0.75rem" },
                            fontWeight: isActive ? 600 : 500,
                          }}
                        >
                          {email?.name
                            ?.split(" ")
                            .map((n) => n[0])
                            .join("")
                            .slice(0, 2)
                            .toUpperCase() || "UN"}
                        </Typography>
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight: isActive ? 500 : 400,
                              fontSize: {
                                xs: "0.8rem",
                                sm: "0.85rem",
                                md: "0.92rem",
                              },
                              color: isActive ? "#1C1F26" : "#818592",
                            }}
                          >
                            {email.name}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: {
                                xs: "0.65rem",
                                sm: "0.68rem",
                                md: "0.7rem",
                              },
                              color: isActive ? "#1C1F26" : "#818592",
                            }}
                          >
                            {email.date}
                          </Typography>
                        </Box>
                        <Typography
                          sx={{
                            fontSize: {
                              xs: "0.75rem",
                              sm: "0.77rem",
                              md: "0.8rem",
                            },
                            color: isActive ? "#1C1F26" : "#818592",
                            fontWeight: isActive ? 500 : 400,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            mt: 0.5,
                          }}
                        >
                          {email.email}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: {
                              xs: "0.75rem",
                              sm: "0.77rem",
                              md: "0.8rem",
                            },
                            fontWeight: isActive ? 500 : 400,
                            color: isActive ? "#1C1F26" : "#818592",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            mt: 0.5,
                          }}
                        >
                          {email.subject}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </InfiniteScroll>
            </Box>
          </>
        )}

        {/* Compose Button */}
        <IconButton
          style={{
            position: "absolute",
            bottom: "10px",
            right: "10px",
            padding: 0,
            background: "transparent",
          }}
          disableRipple
          onClick={() => {
            setSelectedEmail(null);
            setComposeEmail(true);
            setShowEmailList(false);
          }}
        >
          <img
            src={IconConstants.Compose}
            alt="Compose"
            style={{
              zIndex: 1000,
              width: "5rem",
              height: "5rem",
            }}
          />
        </IconButton>
      </Box>

      {/* Email Detail Section */}
      <Box
        sx={{
          width: { xs: "100%", sm: "58%", md: "68%" },
          bgcolor: "#FFFFFF",
          position: "relative",
          display: { xs: showEmailList ? "none" : "block", sm: "block" },
        }}
      >
        {/* Back Button for Mobile */}
        <Box
          sx={{
            p: 1,
            borderBottom: "1px solid #dadce0",
            display: {
              xs: (selectedEmail || composeEmail) ? "flex" : "none",
              sm: "none",
            },
            alignItems: "center",
          }}
        >
          <IconButton onClick={handleBackToList} size="small">
            <ArrowBack />
          </IconButton>
          <Typography sx={{ ml: 1, fontSize: "0.875rem" }}>
            {composeEmail ? "New Message" : "Back to Inbox"}
          </Typography>
        </Box>

        {selectedEmail ? (
          <>
            {/* Email Header */}
            <Box
              sx={{
                p: { xs: "2.1rem", sm: "3.1rem" },
                borderBottom: "1px solid #dadce0",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 2,
                  flexDirection: { xs: "column", sm: "row" },
                  alignItems: { xs: "flex-start", sm: "center" },
                  gap: { xs: 1, sm: 0 },
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 400,
                    fontSize: { xs: "1.15rem", sm: "1.35rem", md: "1.6rem" },
                    wordBreak: "break-word",
                    flex: 1,
                  }}
                >
                  {selectedEmail.subject}
                </Typography>
                <IconButton
                  onClick={() => setReplyEmail(!replyEmail)}
                  sx={{
                    color: "#5f6368",
                    alignSelf: { xs: "flex-end", sm: "center" },
                  }}
                  size="small" 
                >
                  <Reply fontSize="small" />
                </IconButton>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: { xs: "2.3rem", sm: "2.5rem", md: "2.75rem" },
                    height: { xs: "2.3rem", sm: "2.5rem", md: "2.75rem" },
                    borderRadius: "50%",
                    bgcolor: "#F5F6FA",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "1px solid #DFE1EA",
                    flexShrink: 0,
                  }}
                >
                  <Typography
                    sx={{
                      color: "#1C1F26",
                      fontSize: { xs: "0.8rem", sm: "1rem" },
                      fontWeight: 500,
                    }}
                  >
                    {selectedEmail?.name
                      ?.split(" ")
                      .map((n) => n[0])
                      .join("")
                      .slice(0, 2)
                      .toUpperCase() || "UN"}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: { xs: "0.95rem", sm: "1.05rem" },
                      color: "#1C1F26",
                      wordBreak: "break-word",
                    }}
                  >
                    {selectedEmail.name}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: { xs: "0.75rem", sm: "0.8rem" },
                      color: "#818592",
                      wordBreak: "break-all",
                    }}
                  >
                    {selectedEmail.email}
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontSize: { xs: "0.65rem", sm: "0.68rem", md: "0.7rem" },
                    color: "#5f6368",
                    flexShrink: 0,
                  }}
                >
                  {selectedEmail.date}
                </Typography>
              </Box>
            </Box>

            {/* Email Body */}
            <Box
              sx={{
                p: { xs: "2.1rem", sm: "3.1rem" },
                height: { xs: "calc(100vh - 105px)", sm: "calc(100vh - 126px)" },
                overflow: "auto",
                fontSize: { xs: "0.875rem", sm: "1rem" },
                lineHeight: { xs: 1.4, sm: 1.6 },
              }}
            >
              <Box
                sx={{ mt: 1 }}
                dangerouslySetInnerHTML={{
                  __html: selectedEmail.body || selectedEmail.snippet,
                }}
              />
            </Box>

            {/* Reply Section */}
            {replyEmail && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                style={{
                  position: "absolute",
                  top: "30%",
                  width: "90%",
                  left: "5%",
                }}
              >
                <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 8 }}>
                  <TextField
                    multiline
                    rows={4} 
                    fullWidth
                    placeholder="Type your reply..."
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    sx={{
                      mb: 2,
                      "& fieldset": { border: "1px solid #dadce0" },
                      "& .MuiInputBase-input": {
                        fontSize: { xs: "0.875rem", sm: "1rem" },
                      },
                    }}
                  />
                  <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                    <Button
                      variant="contained"
                      onClick={() => handleSendEmail(selectedEmail?.email)}
                      sx={{
                        textTransform: "none",
                        borderRadius: 2,
                        fontSize: { xs: "0.8rem", sm: "0.875rem" },
                        px: { xs: 2, sm: 3 },
                      }}
                    >
                      Send
                    </Button>
                  </Box>
                </Paper>
              </motion.div>
            )}
          </>
        ) : composeEmail ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Box sx={{ width: "95%", mx: "auto", mt: { xs: 2, sm: 3 } }}>
              <Typography
                variant="h6"
                sx={{
                  mb: 2,
                  fontSize: { xs: "1rem", sm: "1.2rem" },
                }}
              >
                New Message
              </Typography>
              <TextField
                label="To"
                fullWidth
                value={composeTo}
                onChange={(e) => setComposeTo(e.target.value)}
                sx={{
                  mb: 2,
                  "& .MuiInputBase-input": {
                    fontSize: { xs: "0.85rem", sm: "1rem" },
                  },
                }}
                size="small" 
              />
              <TextField
                label="Subject"
                fullWidth
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                sx={{
                  mb: 2,
                  "& .MuiInputBase-input": {
                    fontSize: { xs: "0.85rem", sm: "1rem" },
                  },
                }}
                size="small" 
              />
              <TextField
                multiline
                rows={6} 
                fullWidth
                label="Message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                sx={{
                  mb: 2,
                  "& .MuiInputBase-input": {
                    fontSize: { xs: "0.875rem", sm: "1rem" },
                  },
                }}
              />

              {/* Attachments */}
              {attachments.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {attachments.map((file, index) => (
                      <Chip
                        size="small" 
                        key={index}
                        label={file.name}
                        onDelete={() => handleRemoveFile(index)}
                        sx={{
                          fontSize: { xs: "0.7rem", sm: "0.8rem" },
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* Action Buttons */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 1.5,
                  mt: 2,
                }}
              >
                <Button
                  variant="outlined"
                  onClick={() => {
                    setComposeEmail(false);
                    setShowEmailList(true);
                  }}
                  sx={{
                    textTransform: "none",
                    fontSize: { xs: "0.85rem", sm: "0.9rem" },
                  }}
                >
                  Cancel
                </Button>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <input
                    type="file"
                    multiple
                    onChange={handleFileChange}
                    style={{ display: "none" }}
                    id="file-input"
                  />
                  <label htmlFor="file-input">
                    <IconButton
                      component="span"
                      size="small" 
                      sx={{ bgcolor: "transparent" }}
                    >
                      <AttachFile fontSize="small" />
                    </IconButton>
                  </label>
                  <Button
                    variant="contained"
                    onClick={() => handleSendEmail(composeTo)}
                    endIcon={<Send fontSize="small" />}
                    sx={{
                      textTransform: "none",
                      borderRadius: 2,
                      fontSize: { xs: "0.85rem", sm: "0.9rem" },
                      px: { xs: 2, sm: 3 }
                    }}
                  >
                    Send
                  </Button>
                </Box>
              </Box>
            </Box>
          </motion.div>
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "75%",
            }}
          >
            <Typography
              color="text.secondary"
              sx={{
                fontSize: { xs: "0.875rem", sm: "1rem" },
                textAlign: "center",
                px: 2,
              }}
            >
              Select an email to view its content
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default EmailList;